import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { useLocation } from "react-router";
import { useAccessState } from "@/hooks/useAccessState";
import { useQuestionnaireStatus } from "@/hooks/useQuestionnaireStatus";
import { IncompleteQuestionnaireDialog } from "./IncompleteQuestionnaireDialog";
import { allQuestions } from "@/data/onboardingQuestions";

const QUESTIONNAIRE_PROMPT_DISMISSED_KEY = "adigo_questionnaire_prompt_dismissed";
const PROMPT_COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours

interface QuestionnairePromptContextValue {
  showPrompt: () => void;
  hidePrompt: () => void;
}

const QuestionnairePromptContext = createContext<QuestionnairePromptContextValue | null>(null);

export function useQuestionnairePrompt() {
  const context = useContext(QuestionnairePromptContext);
  if (!context) {
    throw new Error("useQuestionnairePrompt must be used within QuestionnairePromptProvider");
  }
  return context;
}

interface QuestionnairePromptProviderProps {
  children: ReactNode;
}

export function QuestionnairePromptProvider({ children }: QuestionnairePromptProviderProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { isLoading, isAuthenticated, user } = useAccessState();
  const { data: status, isLoading: statusLoading } = useQuestionnaireStatus();
  const location = useLocation();

  // Pages where we should NOT show the prompt
  const excludedPaths = [
    "/onboarding",
    "/login",
    "/signup",
    "/forgot-password",
    "/reset-password",
    "/admin",
    "/adigig",
    "/unauthorized",
    "/preview",
  ];

  const isExcludedPath = excludedPaths.some((path) => location.pathname.startsWith(path));

  // Check if prompt was recently dismissed
  const wasRecentlyDismissed = () => {
    const dismissedAt = localStorage.getItem(QUESTIONNAIRE_PROMPT_DISMISSED_KEY);
    if (!dismissedAt) return false;
    
    const dismissedTime = parseInt(dismissedAt, 10);
    return Date.now() - dismissedTime < PROMPT_COOLDOWN_MS;
  };

  // Auto-show prompt when user visits dashboard with incomplete questionnaire
  useEffect(() => {
    if (isLoading || statusLoading || !isAuthenticated) return;
    if (isExcludedPath) return;
    if (!status) return;

    // Suppress if user just completed the questionnaire (handoff window)
    const justCompleted = localStorage.getItem("adigo_questionnaire_just_completed");
    if (justCompleted) {
      const elapsed = Date.now() - parseInt(justCompleted, 10);
      if (elapsed < 30_000) return; // Within 30s — suppress
      localStorage.removeItem("adigo_questionnaire_just_completed");
    }

    // Only show if user has started but not completed the questionnaire
    if (status.hasStarted && !status.isCompleted && !wasRecentlyDismissed()) {
      // Small delay to let the page load first
      const timeout = setTimeout(() => {
        setDialogOpen(true);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [isLoading, statusLoading, isAuthenticated, status, isExcludedPath]);

  const handleOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      // Mark as dismissed for cooldown period
      localStorage.setItem(QUESTIONNAIRE_PROMPT_DISMISSED_KEY, Date.now().toString());
    }
  };

  const showPrompt = () => setDialogOpen(true);
  const hidePrompt = () => handleOpenChange(false);

  const totalQuestions = allQuestions.length;

  return (
    <QuestionnairePromptContext.Provider value={{ showPrompt, hidePrompt }}>
      {children}
      <IncompleteQuestionnaireDialog
        open={dialogOpen}
        onOpenChange={handleOpenChange}
        currentStep={status?.currentStep || 1}
        totalSteps={totalQuestions}
      />
    </QuestionnairePromptContext.Provider>
  );
}
